/**
 * Sentry Client-Side Error Monitoring
 * Initializes Sentry for browser error tracking, performance monitoring,
 * and session replays using the @sentry/react SDK.
 *
 * @module lib/sentry
 */

import * as Sentry from '@sentry/react'
import React from 'react'
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom'
import {
  SENTRY_DSN,
  SENTRY_ENVIRONMENT,
  SENTRY_TRACES_SAMPLE_RATE,
  SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
  SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
} from './env'

/**
 * Initializes Sentry for the client application.
 * Configures React Router v7 browser tracing, session replay, profiling,
 * structured logging, and metrics integrations.
 * Skips initialization if SENTRY_DSN is not configured.
 */
export function initSentryClient(): void {
  if (!SENTRY_DSN) return

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
    ],
    tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
    profileSessionSampleRate: 1.0,
    replaysSessionSampleRate: SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
    sendDefaultPii: true,
    enableLogs: true,
  })
}

/**
 * Sets user context for Sentry events.
 * Associates subsequent errors with the authenticated user.
 *
 * @param userId - The user's unique identifier
 * @param email - The user's email address
 * @param workspaceId - The user's active workspace identifier
 */
export function setSentryUser(userId: string, email?: string, workspaceId?: string): void {
  Sentry.setUser({ id: userId, email })

  if (workspaceId) {
    Sentry.setTag('workspace_id', workspaceId)
  }
}

/**
 * Clears user context from Sentry.
 * Should be called on logout.
 */
export function clearSentryUser(): void {
  Sentry.setUser(null)
}

/** Sentry ErrorBoundary component for wrapping the React tree */
export const SentryErrorBoundary = Sentry.ErrorBoundary

/** HOC to wrap Routes with Sentry routing instrumentation */
export const withSentryRouting = Sentry.withSentryReactRouterV7Routing

/**
 * Sentry structured logger for client-side log ingestion.
 * Sends logs to Sentry's Logs product with severity levels.
 */
export const sentryLogger = Sentry.logger

/** Re-export for breadcrumbs and direct capture */
export { Sentry }
